import { forwardRef, useEffect, useState } from 'react'
import Lottie from 'react-lottie-player'
import { Link, useNavigate } from 'react-router-dom'
import { useMediaQuery } from '@/hooks/useMediaQuery'

import notRecognized from '../animations/not-recognized.json'
import headerBg from '../assets/svgIcons/header-bg-requested.svg'

const ChevronUpIcon = ({ className, ...props }) => (
    <svg viewBox="0 0 24 24" fill="none" className={className} {...props}>
        <path
            d="M18 15L12 9L6 15"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const ChevronDownIcon = ({ className, ...props }) => (
    <svg viewBox="0 0 24 24" fill="none" className={className} {...props}>
        <path
            d="M6 9L12 15L18 9"
            stroke="currentColor"
            strokeWidth="2"
            strokeLinecap="round"
            strokeLinejoin="round"
        />
    </svg>
)

const BrowserEnrollmentAlertBanner = forwardRef(
    ({ setBannerHeight, ...props }, ref) => {
        const navigate = useNavigate()
        const [expanded, setExpanded] = useState(false)
        const isMobile = useMediaQuery('(max-width: 768px)')

        const toggleExpand = () => setExpanded(!expanded)
        const maxHeight = '100px'
        useEffect(() => {
            if (ref && ref.current) {
                const height = expanded
                    ? ref.current.scrollHeight
                    : ref.current.offsetHeight
                setBannerHeight(height)
            }
        }, [expanded, ref, setBannerHeight])

        return (
            <div
                ref={ref}
                className="fixed top-0 left-0 right-0 bg-[#F7E1E1] border-b-[3px] border-[#CB3434] flex items-center justify-center py-4 md:py-6 px-12 md:px-[116px] z-[100000] dark:bg-gray-800 dark:border-b-gray-700 transition-colors"
                style={{
                    backgroundImage: `url(${headerBg})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundRepeat: 'no-repeat',
                }}
            >
                {/* Overlay to reduce pattern visibility - dulls the background squares */}
                <div className="absolute inset-0 bg-white opacity-60 dark:bg-gray-800 dark:opacity-70 pointer-events-none" />
                <div
                    className={`flex items-start md:items-center justify-center w-full relative z-[1] ${
                        isMobile ? 'gap-3' : 'gap-8'
                    }`}
                >
                    <Lottie
                        loop
                        animationData={notRecognized}
                        play={true}
                        style={{
                            width: !isMobile ? '96px' : '48px',
                            height: !isMobile ? '58px' : '28px',
                            minWidth: !isMobile ? '96px' : '48px',
                        }}
                    />
                    {!isMobile && (
                        <p className="text-gray-900 dark:text-gray-100 transition-colors">
                            It looks like you are accessing the platform from an{' '}
                            <span className="font-bold dark:text-gray-100">
                                unrecognized browser
                            </span>
                            . Please use a recognized browser or enroll this
                            browser to unleash the full experience of Kalvium.{' '}
                            <Link
                                onClick={e => {
                                    e.preventDefault()
                                    navigate('/enroll')
                                }}
                                className="underline font-bold text-gray-900 dark:text-blue-400 hover:dark:text-blue-300 transition-colors"
                            >
                                Learn More
                            </Link>
                        </p>
                    )}
                    {isMobile && (
                        <div className="w-full flex items-center mr-0 relative">
                            <p
                                className={`flex-1 text-gray-900 dark:text-gray-100 transition-colors ${
                                    !expanded
                                        ? 'max-h-[100px] overflow-hidden whitespace-nowrap text-ellipsis'
                                        : ''
                                }`}
                            >
                                It looks like you are accessing the platform
                                from an{' '}
                                <span className="font-bold dark:text-gray-100">
                                    unrecognized browser
                                </span>
                                . Please use a recognized browser or enroll this
                                browser to unleash the full experience of
                                Kalvium.{' '}
                                <Link
                                    onClick={e => {
                                        e.preventDefault()
                                        navigate('/enroll')
                                    }}
                                    className="underline font-bold text-gray-900 dark:text-blue-400 hover:dark:text-blue-300 transition-colors"
                                >
                                    Learn More
                                </Link>
                            </p>
                            <button
                                onClick={toggleExpand}
                                className="p-0 self-start dark:text-gray-100 dark:hover:bg-gray-700 transition-colors flex items-center"
                                aria-label={expanded ? 'Collapse' : 'Expand'}
                            >
                                {expanded ? (
                                    <ChevronUpIcon className="w-5 h-5" />
                                ) : (
                                    <ChevronDownIcon className="w-5 h-5" />
                                )}
                            </button>
                        </div>
                    )}
                </div>
            </div>
        )
    }
)

BrowserEnrollmentAlertBanner.displayName = 'BrowserEnrollmentAlertBanner'

export default BrowserEnrollmentAlertBanner
